<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <div class="row g-3 align-items-end mb-4">
        <div class="col-lg-6">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.return_reference") }}</label>
          <div class="d-flex gap-2">
            <input
              type="text"
              class="form-control h-55 bg-white shadow-none border"
              :value="form.return_ref"
              readonly
            />
          </div>
        </div>
        <div class="col-lg-6">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.date") }}</label>
          <input
            type="datetime-local"
            class="form-control h-55 bg-white shadow-none border"
            :value="form.date"
            readonly
          />
        </div>
        <div class="col-12">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.original_sale") }}</label>
          <div class="sale-search-box position-relative">
            <input
              type="text"
              class="form-control h-55 bg-white shadow-none border pe-5"
              :value="saleSearch"
              :placeholder="$t('sales_return_page.original_sale_placeholder')"
              @input="$emit('update:saleSearch', ($event.target as HTMLInputElement).value)"
              @focus="$emit('focus-sale-search')"
            />
            <span class="search-icon">
              <i class="ri-search-line"></i>
            </span>

            <div
              v-if="showSaleResults"
              class="sale-results-list shadow-sm"
            >
              <button
                v-for="sale in saleSearchResults"
                :key="sale.id"
                type="button"
                class="sale-result-item"
                @click="$emit('select-sale', sale)"
              >
                <div>
                  <strong class="d-block text-title">{{ sale.reference_no }}</strong>
                  <span class="text-muted d-block small">
                    {{ sale.customer_name || sale.customer?.name || $t("sales_return_page.walk_in_customer") }}
                  </span>
                </div>
                <span class="sale-meta">
                  {{ sale.warehouse_name || sale.warehouse?.name || $t("sales_return_page.not_available_short") }}
                </span>
              </button>
              <div v-if="!saleSearchResults.length" class="p-3 text-muted text-center small">
                {{ $t("sales_return_page.no_matching_sales_found") }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3 mb-4">
        <div class="col-lg-6">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.sale_reference") }}</label>
          <input
            type="text"
            class="form-control h-55 bg-light shadow-none border"
            :value="form.reference_no"
            readonly
          />
        </div>
        <div class="col-lg-6">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.customer") }}</label>
          <input
            type="text"
            class="form-control h-55 bg-light shadow-none border"
            :value="form.customer_name"
            readonly
          />
        </div>
        <div class="col-lg-6">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.warehouse") }}</label>
          <input
            type="text"
            class="form-control h-55 bg-light shadow-none border"
            :value="form.warehouse_name"
            readonly
          />
        </div>
        <div class="col-lg-6">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.status") }}</label>
          <select
            class="form-select h-55 bg-white shadow-none border"
            :value="form.status"
            @change="$emit('update:status', ($event.target as HTMLSelectElement).value)"
          >
            <option value="completed">{{ $t("sales_return_page.status_completed") }}</option>
            <option value="pending">{{ $t("sales_return_page.status_pending") }}</option>
          </select>
        </div>
      </div>

      <div class="card border-0 bg-light-subtle rounded-3 mb-4">
        <div class="card-body p-3 p-xl-4">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h6 class="mb-1">{{ $t("sales_return_page.returned_items") }}</h6>
              <p class="text-muted mb-0 small">{{ $t("sales_return_page.select_return_quantities") }}</p>
            </div>
            <span class="badge bg-white text-dark border px-3 py-2">
              {{ $t("sales_return_page.items_count", { count: totalItems }) }}
            </span>
          </div>

          <div class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>{{ $t("sales_return_page.product") }}</th>
                  <th>{{ $t("sales_return_page.code") }}</th>
                  <th class="text-center">{{ $t("sales_return_page.sold_qty") }}</th>
                  <th class="text-center">{{ $t("sales_return_page.return_qty") }}</th>
                  <th class="text-end">{{ $t("sales_return_page.unit_price") }}</th>
                  <th class="text-end">{{ $t("sales_return_page.subtotal") }}</th>
                  <th class="text-end">{{ $t("sales_return_page.delete") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!items.length">
                  <td colspan="7" class="text-center text-muted py-4">
                    {{ $t("sales_return_page.select_original_sale_hint") }}
                  </td>
                </tr>
                <tr v-for="item in items" :key="item.product_id">
                  <td>{{ item.product_name }}</td>
                  <td>{{ item.product_code }}</td>
                  <td class="text-center">
                    <span class="badge bg-light text-dark border">{{ item.max_quantity }}</span>
                  </td>
                  <td class="text-center">
                    <div class="qty-stepper mx-auto">
                      <button type="button" class="qty-btn" @click="$emit('update-qty', item.product_id, item.quantity - 1)">
                        -
                      </button>
                      <input
                        type="number"
                        min="1"
                        :max="item.max_quantity"
                        class="qty-input"
                        :value="item.quantity"
                        @change="$emit('update-qty', item.product_id, Number(($event.target as HTMLInputElement).value))"
                      />
                      <button type="button" class="qty-btn" @click="$emit('update-qty', item.product_id, item.quantity + 1)">
                        +
                      </button>
                    </div>
                  </td>
                  <td class="text-end">${{ Number(item.unit_price || 0).toFixed(2) }}</td>
                  <td class="text-end">${{ Number(item.subtotal || 0).toFixed(2) }}</td>
                  <td class="text-end">
                    <button
                      type="button"
                      class="btn p-0 border-0 text-danger"
                      @click="$emit('remove-item', item.product_id)"
                    >
                      <i class="ri-delete-bin-line fs-18"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-lg-4">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.discount") }}</label>
          <div class="input-group">
            <input
              type="number"
              min="0"
              class="form-control h-55 bg-white shadow-none border"
              :value="form.discount"
              @input="$emit('update:discount', Number(($event.target as HTMLInputElement).value))"
            />
            <span class="input-group-text bg-light border">$</span>
          </div>
        </div>
        <div class="col-lg-4">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.tax") }}</label>
          <div class="input-group">
            <input
              type="number"
              min="0"
              class="form-control h-55 bg-white shadow-none border"
              :value="form.tax"
              @input="$emit('update:tax', Number(($event.target as HTMLInputElement).value))"
            />
            <span class="input-group-text bg-light border">$</span>
          </div>
        </div>
        <div class="col-lg-4">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.shipping") }}</label>
          <div class="input-group">
            <input
              type="number"
              min="0"
              class="form-control h-55 bg-white shadow-none border"
              :value="form.shipping"
              @input="$emit('update:shipping', Number(($event.target as HTMLInputElement).value))"
            />
            <span class="input-group-text bg-light border">$</span>
          </div>
        </div>
        <div class="col-lg-8">
          <label class="form-label fs-14 text-title">{{ $t("sales_return_page.notes") }}</label>
          <textarea
            class="form-control bg-white shadow-none border resize-none"
            rows="5"
            :value="form.note"
            :placeholder="$t('sales_return_page.notes_placeholder')"
            @input="$emit('update:note', ($event.target as HTMLTextAreaElement).value)"
          ></textarea>
        </div>
        <div class="col-lg-4">
          <div class="summary-card h-100">
            <div class="summary-row">
              <span>{{ $t("sales_return_page.subtotal") }}</span>
              <strong>${{ Number(subTotal || 0).toFixed(2) }}</strong>
            </div>
            <div class="summary-row">
              <span>{{ $t("sales_return_page.discount") }}</span>
              <strong>${{ Number(form.discount || 0).toFixed(2) }}</strong>
            </div>
            <div class="summary-row">
              <span>{{ $t("sales_return_page.tax") }}</span>
              <strong>${{ Number(form.tax || 0).toFixed(2) }}</strong>
            </div>
            <div class="summary-row">
              <span>{{ $t("sales_return_page.shipping") }}</span>
              <strong>${{ Number(form.shipping || 0).toFixed(2) }}</strong>
            </div>
            <div class="summary-row grand">
              <span>{{ $t("sales_return_page.grand_total") }}</span>
              <strong>${{ Number(grandTotal || 0).toFixed(2) }}</strong>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-4">
        <button
          type="button"
          class="btn style-one d-inline-flex align-items-center px-4"
          :disabled="isSubmitting"
          @click="$emit('submit')"
        >
          <i class="ri-save-line me-2"></i>
          {{ $t(submitLabel) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { Sale } from "@/services/sales";

export default defineComponent({
  name: "SalesReturnForm",
  props: {
    form: {
      type: Object as PropType<{
        date: string;
        return_ref: string;
        note: string;
        status: string;
        discount: number;
        tax: number;
        shipping: number;
        customer_name: string;
        warehouse_name: string;
        reference_no: string;
      }>,
      required: true,
    },
    saleSearch: {
      type: String,
      default: "",
    },
    saleSearchResults: {
      type: Array as PropType<Sale[]>,
      default: () => [],
    },
    showSaleResults: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array as PropType<
        Array<{
          product_id: number;
          product_code: string;
          product_name: string;
          quantity: number;
          max_quantity: number;
          unit_price: number;
          subtotal: number;
        }>
      >,
      default: () => [],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    subTotal: {
      type: Number,
      default: 0,
    },
    grandTotal: {
      type: Number,
      default: 0,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: "sales_return_page.submit_sales_return",
    },
  },
  emits: [
    "update:saleSearch",
    "focus-sale-search",
    "select-sale",
    "update:status",
    "update:discount",
    "update:tax",
    "update:shipping",
    "update:note",
    "update-qty",
    "remove-item",
    "submit",
  ],
});
</script>

<style scoped>
.sale-search-box {
  position: relative;
}

.search-icon {
  position: absolute;
  right: 16px;
  top: 18px;
  color: #8f9bb3;
  pointer-events: none;
}

.sale-results-list {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 20;
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.sale-result-item {
  width: 100%;
  border: 0;
  background: transparent;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  text-align: left;
}

.sale-result-item + .sale-result-item {
  border-top: 1px solid #eef2f7;
}

.sale-result-item:hover {
  background: #f8faff;
}

.sale-meta {
  color: #6b7280;
  font-size: 12px;
  white-space: nowrap;
}

.qty-stepper {
  width: 130px;
  display: grid;
  grid-template-columns: 36px 1fr 36px;
  align-items: center;
  border: 1px solid #dde4ee;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.qty-btn {
  height: 38px;
  border: 0;
  background: #eef2ff;
  color: #5d5fef;
  font-weight: 700;
}

.qty-input {
  width: 100%;
  height: 38px;
  border: 0;
  text-align: center;
  outline: none;
}

.summary-card {
  border: 1px solid #e6ebf2;
  border-radius: 16px;
  background: #fff;
  padding: 20px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #edf2f7;
}

.summary-row.grand {
  border-bottom: 0;
  padding-bottom: 0;
  color: #5d5fef;
  font-size: 18px;
}
</style>
